import React, { useState, useEffect } from 'react';
import Select from 'react-select';
// Admin.styles.css is still imported but most styles will be overridden by Tailwind classes
import '../../pages/Admin/Admin.styles.css';
import { API_config } from '../../config/apiConfig';

/**
 * Format local datetime string (YYYY-MM-DDTHH:MM) to API format (YYYY-MM-DD HH:MM:SS)
 * @param {string} dateString 
 * @returns {string}
 */
const formatForApi = (dateString) => {
    if (!dateString) return '';
    return dateString.replace('T', ' ') + ':00';
};

/**
 * Get today's date in YYYY-MM-DDTHH:MM format
 * @param {boolean} endOfDay 
 * @returns {string}
 */
const getDefaultDate = (endOfDay = false) => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const time = endOfDay ? '23:59' : '00:00';
    return `${year}-${month}-${day}T${time}`;
};

const ReccePenalty = () => {
    // State
    const [geofences, setGeofences] = useState([]);
    const [selectedGeofences, setSelectedGeofences] = useState([]);
    const [fromDate, setFromDate] = useState(getDefaultDate(false));
    const [toDate, setToDate] = useState(getDefaultDate(true));
    const [searchQuery, setSearchQuery] = useState('');
    const [showOnlyOverLimit, setShowOnlyOverLimit] = useState(false);

    // Loading & Error States
    const [loadingGeofences, setLoadingGeofences] = useState(false);
    const [loadingReport, setLoadingReport] = useState(false);
    const [error, setError] = useState(null);
    const [reportData, setReportData] = useState(null);

    // Fetch Geofences on Mount
    useEffect(() => {
        const fetchGeofences = async () => {
            setLoadingGeofences(true);
            setError(null);
            try {
                const token = API_config.bearerToken;
                const response = await fetch(`${API_config.recceApiUrl}?type=geofences`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (response.status === 401) throw new Error('Unauthorized');
                if (!response.ok) throw new Error(`API Error: ${response.status}`);

                const data = await response.json();
                if (data.success && Array.isArray(data.items)) {
                    setGeofences(data.items.map(item => ({
                        value: item.id,
                        label: item.name
                    })));
                }
            } catch (err) {
                console.error('Error fetching geofences:', err);
                setError(err.message);
            } finally {
                setLoadingGeofences(false);
            }
        };
        fetchGeofences();
    }, []);

    const handleGenerateReport = async () => {
        if (selectedGeofences.length === 0) {
            setError('Please select at least one geofence');
            return;
        }
        if (fromDate > toDate) {
            setError('"From" date cannot be after "To" date');
            return;
        }

        setLoadingReport(true);
        setError(null);
        setReportData(null);

        try {
            const token = API_config.bearerToken;
            const fromFormatted = formatForApi(fromDate);
            const toFormatted = formatForApi(toDate);
            const geofenceIds = selectedGeofences.map(g => g.value).join(',');

            // Use speed_gates_report endpoint with user specified params
            const url = `${API_config.recceApiUrl}?type=speed_gates_report&geofenceId=${geofenceIds}&from=${encodeURIComponent(fromFormatted)}&to=${encodeURIComponent(toFormatted)}&tz=1&maxWindowMin=5&limit=1000&offset=0`;

            console.log('Fetching report from:', url);

            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) throw new Error(`API Error: ${response.status}`);

            const contentType = response.headers.get("content-type");
            if (!contentType || !contentType.includes("application/json")) {
                const text = await response.text();
                console.error('API Error: Non-JSON response received');
                if (text.trim().startsWith('<!DOCTYPE')) {
                    throw new Error('Server returned HTML instead of JSON (likely 404/500). Check API URL.');
                }
                throw new Error('Received non-JSON response');
            }

            const data = await response.json();

            if (data.success) {
                // Ensure rows exist
                const rows = data.rows || [];
                setReportData({ ...data, rows });
            } else {
                throw new Error(data.message || 'Failed to fetch report');
            }
        } catch (err) {
            console.error('Error fetching report:', err);
            setError(err.message);
        } finally {
            setLoadingReport(false);
        }
    };

    const exportCSV = () => {
        if (!reportData || !reportData.rows) return;

        // Define headers
        let csvContent = "ID,Date,Miejsce,Device ID,Number,Name,Contact,Start Time,End Time,Time (s),Speed (km/h),Max Speed,Violated Limit,Exceeded By (km/h),Penalty Points\n";

        // Add rows
        reportData.rows.forEach(row => {
            const rowString = [
                row.eventid,
                `"${row.pomiarSTART ? row.pomiarSTART.substring(0, 16) : ''}"`,
                `"${row.Miejsce || ''}"`,
                row.deviceid,
                `"${row.Number || ''}"`,
                `"${row.Names || ''}"`,
                `"${row.contact || ''}"`,
                row.pomiarSTART,
                row.pomiarEND,
                row.czas,
                row.avg_speed_kmh,
                row.max_speed_kmh,
                row.ToFast ? 'YES' : 'NO',
                row.ToFast || 0,
                row.howmuch
            ].join(',');
            csvContent += rowString + "\n";
        });

        // Trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `recce_penalty_${new Date().toISOString().slice(0, 10)}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Filter rows based on search
    const filteredRows = reportData?.rows?.filter(row => {
        const query = searchQuery.toLowerCase();
        const matchesSearch = (
            (row.Names || '').toLowerCase().includes(query) ||
            String(row.deviceid).includes(query) ||
            String(row.Number).includes(query) ||
            (row.Miejsce || '').toLowerCase().includes(query)
        );

        if (showOnlyOverLimit) {
            return matchesSearch && (row.ToFast && parseFloat(row.ToFast) > 0);
        }

        return matchesSearch;
    }) || [];

    // Helper to format names
    const formatNames = (namesStr) => {
        if (!namesStr) return '-';
        // Split by / or \
        const parts = namesStr.split(/[/\\]/);
        return (
            <div className="flex flex-col leading-tight">
                <span className="font-bold text-[var(--text-primary)]">{parts[0]?.trim()}</span>
                {parts[1] && <span className="text-xs text-[var(--text-secondary)] mt-0.5">{parts[1].trim()}</span>}
            </div>
        );
    };

    // Custom Styles for React Select - Updated for Dark Mode
    const customStyles = {
        control: (base, state) => ({
            ...base,
            borderColor: 'var(--border-color)',
            backgroundColor: 'var(--bg-tertiary)',
            color: 'var(--text-primary)',
            minHeight: '42px',
            borderRadius: '0.5rem', // rounded-lg
            boxShadow: 'none',
            '&:hover': { borderColor: '#3b82f6' }, // blue-500
        }),
        menu: (base) => ({
            ...base,
            zIndex: 9999, // Ensure it's above everything
            backgroundColor: 'var(--bg-secondary)',
            borderColor: 'var(--border-color)',
            borderRadius: '0.5rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            color: 'var(--text-primary)'
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isFocused ? 'var(--hover-bg)' : 'transparent',
            color: 'var(--text-primary)',
            cursor: 'pointer'
        }),
        singleValue: (base) => ({
            ...base,
            color: 'var(--text-primary)'
        }),
        input: (base) => ({
            ...base,
            color: 'var(--text-primary)'
        }),
        multiValue: (base) => ({
            ...base,
            backgroundColor: 'var(--active-bg)',
            borderRadius: '0.25rem',
        }),
        multiValueLabel: (base) => ({
            ...base,
            color: '#1d4ed8', // blue-700
            fontWeight: 500,
            fontSize: '0.75rem',
        }),
        multiValueRemove: (base) => ({
            ...base,
            color: '#1d4ed8',
            ':hover': { backgroundColor: 'transparent', color: '#1e40af' },
        }),
        menuPortal: base => ({ ...base, zIndex: 9999 })
    };

    return (
        <div className="min-h-screen p-6 max-w-[1400px] mx-auto text-[var(--text-primary)] font-sans">
            {/* Header */}
            <header className="mb-6 flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-[var(--text-primary)]">Recce Penalty Report</h1>
                    <p className="text-sm text-[var(--text-secondary)]">Speeding violations and penalty analysis report</p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={exportCSV}
                        disabled={!reportData}
                        className="flex items-center gap-2 bg-[var(--bg-secondary)] border border-[var(--border-color)] px-4 py-2 rounded-lg text-sm font-medium hover:bg-[var(--hover-bg)] transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed text-[var(--text-primary)]"
                    >
                        <span className="material-icons-outlined text-[20px]">download</span>
                        Export CSV
                    </button>
                </div>
            </header>

            {/* Error Message */}
            {error && (
                <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg relative dark:bg-red-900/20 dark:border-red-800 dark:text-red-400" role="alert">
                    <strong className="font-bold">Error: </strong>
                    <span className="block sm:inline">{error}</span>
                </div>
            )}

            {/* Filter Card */}
            <div className="bg-[var(--bg-secondary)] rounded-xl shadow-sm border border-[var(--border-color)] p-5 mb-6 transition-colors">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                    {/* Geofence Selection */}
                    <div className="md:col-span-3">
                        <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-2">Geofence Selection</label>
                        <Select
                            isMulti
                            options={geofences}
                            value={selectedGeofences}
                            onChange={setSelectedGeofences}
                            isLoading={loadingGeofences}
                            isDisabled={loadingReport}
                            placeholder="Select geofences..."
                            className="react-select-container text-sm"
                            classNamePrefix="react-select"
                            menuPortalTarget={document.body}
                            styles={customStyles}
                            components={{
                                IndicatorSeparator: () => null
                            }}
                        />
                    </div>

                    {/* From Date */}
                    <div className="md:col-span-2">
                        <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-2">From</label>
                        <div className="relative">
                            <input
                                type="datetime-local"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                className="w-full border border-[var(--border-color)] rounded-lg text-sm bg-[var(--bg-tertiary)] focus:ring-blue-500 focus:border-blue-500 p-2.5 h-[42px] text-[var(--text-primary)]"
                            />
                        </div>
                    </div>

                    {/* To Date */}
                    <div className="md:col-span-2">
                        <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-2">To</label>
                        <div className="relative">
                            <input
                                type="datetime-local"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                className="w-full border border-[var(--border-color)] rounded-lg text-sm bg-[var(--bg-tertiary)] focus:ring-blue-500 focus:border-blue-500 p-2.5 h-[42px] text-[var(--text-primary)]"
                            />
                        </div>
                    </div>

                    {/* Generate Button */}
                    <div className="md:col-span-1 flex justify-end">
                        <button
                            onClick={handleGenerateReport}
                            disabled={loadingReport || selectedGeofences.length === 0}
                            className="bg-blue-600 hover:bg-blue-700 text-white w-full h-[42px] rounded-lg font-semibold text-sm flex items-center justify-center gap-2 transition-colors shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
                            title="Search / Generate Report"
                        >
                            {loadingReport ? (
                                <span className="material-icons-outlined animate-spin text-[24px]">refresh</span>
                            ) : (
                                <span className="material-icons-outlined text-[24px]">search</span>
                            )}
                        </button>
                    </div>

                    {/* Over Limit Filter */}
                    <div className="md:col-span-2 flex items-center h-[42px]">
                        <label className="inline-flex items-center cursor-pointer select-none">
                            <input
                                type="checkbox"
                                className="form-checkbox h-5 w-5 text-red-600 rounded border-[var(--border-color)] focus:ring-red-500 transition duration-150 ease-in-out bg-[var(--bg-tertiary)]"
                                checked={showOnlyOverLimit}
                                onChange={(e) => setShowOnlyOverLimit(e.target.checked)}
                            />
                            <span className="ml-2 text-sm font-semibold text-[var(--text-primary)]">Show Violations Only</span>
                        </label>
                    </div>

                    {/* Search Input */}
                    <div className="md:col-span-2">
                        <div className="relative">
                            <input
                                className="w-full pl-8 pr-3 py-2 border border-[var(--border-color)] rounded-lg text-sm bg-[var(--bg-tertiary)] focus:ring-blue-500 focus:border-blue-500 h-[42px] text-[var(--text-primary)] placeholder-[var(--text-tertiary)]"
                                placeholder="Filter results..."
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                disabled={!reportData}
                            />
                            <span className="material-icons-outlined absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] text-[18px]">filter_alt</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Results Table */}
            {reportData && (
                <div className="bg-[var(--bg-secondary)] rounded-xl shadow-sm border border-[var(--border-color)] overflow-hidden transition-colors">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-[var(--bg-tertiary)] text-xs text-[var(--text-secondary)] uppercase font-bold border-b border-[var(--border-color)]">
                                <tr>
                                    <th className="px-4 py-3 whitespace-nowrap w-[140px]">DATE</th>
                                    <th className="px-4 py-3 w-[25%]">MIEJSCE</th>
                                    <th className="px-4 py-3 text-center w-[80px]">NO.</th>
                                    <th className="px-4 py-3 w-[25%]">CREW</th>
                                    <th className="px-4 py-3 text-center whitespace-nowrap">LIMIT</th>
                                    <th className="px-4 py-3 text-center whitespace-nowrap">MAX</th>
                                    <th className="px-4 py-3 text-center whitespace-nowrap">OVER</th>
                                    <th className="px-4 py-3 text-right whitespace-nowrap">PENALTY</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[var(--border-color)]">
                                {filteredRows.length > 0 ? (
                                    filteredRows.map((row, idx) => (
                                        <tr key={idx} className="hover:bg-[var(--hover-bg)] transition-colors">
                                            <td className="px-4 py-3 font-mono text-[var(--text-secondary)] text-xs whitespace-nowrap">
                                                {row.pomiarSTART ? row.pomiarSTART.substring(0, 16) : '-'}
                                            </td>
                                            <td className="px-4 py-3 font-medium text-[var(--text-primary)] text-xs">{row.Miejsce}</td>
                                            <td className="px-4 py-3 font-bold text-[var(--text-primary)] text-center text-lg">#{row.Number}</td>
                                            <td className="px-4 py-3">
                                                {formatNames(row.Names)}
                                            </td>
                                            <td className="px-4 py-3 text-center text-[var(--text-secondary)] font-mono text-xs">
                                                {Math.round(row.SpeedLimit)} km/h
                                            </td>
                                            <td className="px-4 py-3 text-center font-bold text-[var(--text-primary)]">
                                                {row.max_speed_kmh}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                {row.ToFast ? (
                                                    <span className="bg-red-50 text-red-600 px-2 py-0.5 rounded text-xs font-bold border border-red-100 whitespace-nowrap dark:bg-red-900/40 dark:text-red-400 dark:border-red-800">
                                                        +{parseFloat(row.ToFast).toFixed(1)}
                                                    </span>
                                                ) : (
                                                    <span className="text-[var(--text-tertiary)]">-</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                {row.howmuch > 0 ? (
                                                    <span className="text-red-700 font-bold bg-amber-100 px-2 py-1 rounded border border-amber-200 text-xs whitespace-nowrap dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800">
                                                        {row.howmuch} pts
                                                    </span>
                                                ) : (
                                                    <span className="text-[var(--text-tertiary)]">-</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="8" className="px-6 py-12 text-center text-[var(--text-tertiary)]">
                                            <span className="material-icons-outlined text-4xl mb-2 block">search_off</span>
                                            No records found based on current filters.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="px-6 py-4 border-t border-[var(--border-color)] text-sm text-[var(--text-secondary)] flex justify-between items-center">
                        <span>Showing <span className="font-semibold text-[var(--text-primary)]">{filteredRows.length}</span> of {reportData.total || 0} records</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReccePenalty;
